/**
 * DebouncedUsernameChecker — live "is this name taken" checks while typing.
 *
 * Port of Phone Dice's RequestTimer pattern (PhoneDiceWeb/framework/
 * RequestTimer.js, used by LoginScreenV2 for check-username):
 *   - every keystroke RESETS the debounce timer, cancelling the pending query
 *   - each sent query carries a sequence id; responses for anything but the
 *     LATEST id are dropped (a slow old response can never overwrite the
 *     verdict for what's currently in the input)
 *   - cancel() invalidates everything in flight (modal closed, input cleared)
 *
 * Transport-agnostic: `sendQuery` fires the socket event; the socket's result
 * listener feeds `handleResult`. The check is advisory UX — the server
 * re-validates on submit.
 */

export type UsernameCheckCallback = (available: boolean, username: string) => void;

export class DebouncedUsernameChecker {
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Monotonic sequence; only the response matching the latest sent id counts. */
  private seq = 0;
  private callback: UsernameCheckCallback | null = null;

  constructor(
    private readonly sendQuery: (requestId: number, username: string) => void,
    private readonly debounceMs = 300,
  ) {}

  /** Schedule a check for `username`, cancelling any pending/in-flight one. */
  query(username: string, callback: UsernameCheckCallback): void {
    this.cancel();
    this.callback = callback;
    const id = ++this.seq;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.sendQuery(id, username);
    }, this.debounceMs);
  }

  /** Feed a server response in; stale request ids are silently dropped. */
  handleResult(requestId: number, available: boolean, username: string): void {
    if (requestId !== this.seq || !this.callback) return;
    const cb = this.callback;
    this.callback = null;
    cb(available, username);
  }

  /** Drop the pending timer AND invalidate any in-flight response. */
  cancel(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.seq++; // any response for an earlier id is now stale
    this.callback = null;
  }
}
